import { useTranslation } from 'react-i18next'
import { WarningIcon } from '@tractian/icons'

import type { HubspotDuplicate } from '@/features/assessments/api/hubspot-search'
import { paths } from '@/routes/routes'

export const HUBSPOT_DUPLICATE_WARNING_MAX_LINKS = 3

export type HubspotDuplicateWarningProps = {
  duplicates: HubspotDuplicate[]
}

// Informational only: an existing assessment never blocks creating another one.
export function HubspotDuplicateWarning({ duplicates }: HubspotDuplicateWarningProps) {
  const { t } = useTranslation('plantAssessment')
  if (duplicates.length === 0) return null

  const shown = duplicates.slice(0, HUBSPOT_DUPLICATE_WARNING_MAX_LINKS)
  const remaining = duplicates.length - shown.length

  return (
    <div
      role="status"
      data-testid="hubspot-duplicate-warning"
      className="flex gap-2 rounded bg-yellow-100 px-3 py-2 text-sm text-yellow-900"
    >
      <WarningIcon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <div className="flex min-w-0 flex-col gap-1">
        <span>{t('hubspotDuplicateWarning.title')}</span>
        <ul className="flex flex-col gap-0.5">
          {shown.map(duplicate => (
            <li key={duplicate.plantAssessmentId} className="truncate">
              <a
                href={paths.assessmentDetail(duplicate.plantAssessmentId)}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium underline"
              >
                {duplicate.plantName}
              </a>
            </li>
          ))}
        </ul>
        {remaining > 0 && (
          <span>{t('hubspotDuplicateWarning.andMore', { count: remaining })}</span>
        )}
      </div>
    </div>
  )
}
